/**
 * UsersTab Component
 * User management within Admin page
 */

import React, { useState } from 'react';
import { useOpenWithReturn } from '../../../hooks/useReturnNavigation';
import { useUrlState } from '../../../hooks/useUrlState';
import { UserPlus, Search, Mail, Trash2, Edit, Upload, UserMinus, UserCheck, RotateCcw } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import {
  useUsers,
  useDeleteUser,
  useHardDeleteUser,
  useDeactivateUser,
  useReactivateUser,
  useRestoreUser,
  useResendInvitation,
} from '../../../hooks';
import { Card, Button, Input, Badge, LoadingSpinner, Avatar, EmptyState, ImportModal } from '../../../components/common';
import type { SafeUser, UserRole, UserStatus } from '../../../types';

export const UsersTab: React.FC = () => {
  const openWithReturn = useOpenWithReturn();
  // Search, role, status and "show deleted" live in the URL, so editing a user and coming
  // Back keeps them.
  const [view, setView] = useUrlState(
    { q: '', role: '', status: '', deleted: '0' },
    { role: ['', ...['account_admin', 'manager', 'employee', 'viewer']], status: ['', ...['active', 'invited', 'inactive']], deleted: ['0', '1'] }
  );
  const searchQuery = view.q;
  const roleFilter = view.role as UserRole | '';
  const statusFilter = view.status as UserStatus | '';
  const setSearchQuery = (q: string) => setView({ q });
  const setRoleFilter = (role: UserRole | '') => setView({ role });
  const setStatusFilter = (status: UserStatus | '') => setView({ status });
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const showDeleted = view.deleted === '1';
  const setShowDeleted = (on: boolean) => setView({ deleted: on ? '1' : '0' });

  const { data, isLoading, refetch } = useUsers({
    search: searchQuery || undefined,
    role: roleFilter || undefined,
    status: statusFilter || undefined,
    includeDeleted: showDeleted || undefined,
  });

  const deleteUserMutation = useDeleteUser();
  const hardDeleteUserMutation = useHardDeleteUser();
  const deactivateUserMutation = useDeactivateUser();
  const reactivateUserMutation = useReactivateUser();
  const restoreUserMutation = useRestoreUser();
  const resendInvitationMutation = useResendInvitation();

  const handleDeactivate = async (user: SafeUser) => {
    if (!window.confirm(`Deactivate ${user.firstName} ${user.lastName}? They will lose access but remain in the list.`)) return;
    try {
      await deactivateUserMutation.mutateAsync(user.id);
      refetch();
    } catch {
      /* toast handled in hook */
    }
  };

  const handleReactivate = async (user: SafeUser) => {
    try {
      await reactivateUserMutation.mutateAsync(user.id);
      refetch();
    } catch {
      /* toast handled in hook */
    }
  };

  const handleSoftDelete = async (user: SafeUser) => {
    if (!window.confirm(`Delete ${user.firstName} ${user.lastName}? They will be hidden from the list but can be restored later.`)) return;
    try {
      await deleteUserMutation.mutateAsync(user.id);
      refetch();
    } catch {
      /* toast handled in hook */
    }
  };

  const handleHardDelete = async (user: SafeUser) => {
    if (!window.confirm(`Permanently delete the invitation for ${user.firstName} ${user.lastName} (${user.email})? This CANNOT be undone. You can re-invite this email afterwards.`)) return;
    try {
      await hardDeleteUserMutation.mutateAsync(user.id);
      refetch();
    } catch {
      /* toast handled in hook */
    }
  };

  const handleRestore = async (user: SafeUser) => {
    try {
      await restoreUserMutation.mutateAsync(user.id);
      refetch();
    } catch {
      /* toast handled in hook */
    }
  };

  const handleResendInvitation = async (userId: string) => {
    try {
      await resendInvitationMutation.mutateAsync(userId);
      toast.success('Invitation resent successfully');
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'account_admin':
        return 'primary';
      case 'manager':
        return 'warning';
      case 'employee':
        return 'success';
      case 'viewer':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'invited':
        return 'warning';
      case 'inactive':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const formatRole = (role: string) => {
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Users</h2>
          <p className="text-sm text-stone-500">
            {data?.total || 0} users in your organization
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => setIsImportModalOpen(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import Users
          </Button>
          <Button variant="primary" onClick={() => openWithReturn('/users/invite')}>
            <UserPlus className="w-4 h-4 mr-2" />
            Invite User
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as UserRole | '')}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Roles</option>
            <option value="account_admin">Account Admin</option>
            <option value="manager">Manager</option>
            <option value="employee">Employee</option>
            <option value="viewer">Viewer</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as UserStatus | '')}
            className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="inactive">Inactive</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-stone-600 whitespace-nowrap">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded border-stone-300 text-primary-600 focus:ring-primary-500"
            />
            Show deleted
          </label>
        </div>
      </Card>

      {/* Users List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !data?.users.length ? (
        <EmptyState
          icon={UserPlus}
          title="No users found"
          description={searchQuery || roleFilter || statusFilter
            ? "No users match your search criteria"
            : "Invite your first team member to get started"
          }
          action={{
            label: 'Invite User',
            onClick: () => openWithReturn('/users/invite'),
          }}
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-stone-200">
              <thead className="bg-sand">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-stone-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-stone-200">
                {data.users.map((user) => (
                  <tr key={user.id} className="hover:bg-sand">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Avatar
                          size="sm"
                          fallbackText={`${user.firstName} ${user.lastName}`}
                        />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-stone-900">
                            {user.firstName} {user.lastName}
                          </div>
                          {/* Email is absent unless the caller is entitled to it. */}
                          {(user.email || user.department) && (
                            <div className="text-sm text-stone-500">
                              {user.email || user.department}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getRoleBadgeVariant(user.role)}>
                        {formatRole(user.role)}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                      {user.department || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.deletedAt ? (
                        <Badge variant="danger">deleted</Badge>
                      ) : (
                        <Badge variant={getStatusBadgeVariant(user.status)}>
                          {user.status}
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-stone-500">
                      {user.lastLoginAt
                        ? formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true })
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {user.deletedAt ? (
                          <button
                            onClick={() => handleRestore(user)}
                            className="text-stone-400 hover:text-primary-600"
                            title="Restore user"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        ) : (user.status === 'invited' || user.status === 'pending_activation') ? (
                          <>
                            <button
                              onClick={() => handleResendInvitation(user.id)}
                              className="text-stone-400 hover:text-primary-600"
                              title="Resend invitation"
                            >
                              <Mail className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleHardDelete(user)}
                              className="text-stone-400 hover:text-danger-600"
                              title="Delete invitation permanently"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : user.status === 'active' ? (
                          <>
                            <button
                              onClick={() => openWithReturn(`/users/${user.id}/edit`)}
                              className="text-stone-400 hover:text-primary-600"
                              title="Edit user"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeactivate(user)}
                              className="text-stone-400 hover:text-amber-600"
                              title="Deactivate user"
                            >
                              <UserMinus className="w-4 h-4" />
                            </button>
                          </>
                        ) : user.status === 'inactive' ? (
                          <>
                            <button
                              onClick={() => openWithReturn(`/users/${user.id}/edit`)}
                              className="text-stone-400 hover:text-primary-600"
                              title="Edit user"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReactivate(user)}
                              className="text-stone-400 hover:text-green-600"
                              title="Reactivate user"
                            >
                              <UserCheck className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleSoftDelete(user)}
                              className="text-stone-400 hover:text-danger-600"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          /* Unknown / out-of-scope status (e.g. suspended): inspect only */
                          <button
                            onClick={() => openWithReturn(`/users/${user.id}/edit`)}
                            className="text-stone-400 hover:text-primary-600"
                            title="Edit user"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Import Modal */}
      <ImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        type="users"
        onImportComplete={() => {
          refetch();
          setIsImportModalOpen(false);
        }}
      />
    </div>
  );
};
